/**
 * Customer PWA i18n bootstrap.
 *
 * Stack: `react-i18next` + `i18next`. Namespace is `customer`.
 * Wrap the app root with `<I18nextProvider i18n={i18n}>`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import csCustomer from './locales/cs/customer.json';
import enCustomer from './locales/en/customer.json';
import skCustomer from './locales/sk/customer.json';
import pseudoCustomer from './locales/pseudo/customer.json';
import {
  readStoredCustomerLocale,
  writeStoredCustomerLocale,
  type CustomerSupportedLocale,
} from './shared/customerLocaleStorage.js';

function normalizeCustomerLocale(lng: string): CustomerSupportedLocale {
  if (lng.startsWith('en')) {
    return 'en';
  }
  if (lng.startsWith('sk')) {
    return 'sk';
  }
  return 'cs';
}

const storedLocale = readStoredCustomerLocale();
const urlLng =
  typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('lng')
    : null;
const initialLng: CustomerSupportedLocale | 'pseudo' =
  urlLng === 'pseudo' ? 'pseudo' : storedLocale ?? 'cs';

void i18n
  .use(initReactI18next)
  .init({
    lng: initialLng,
    fallbackLng: 'cs',
    supportedLngs: ['cs', 'en', 'sk', 'pseudo'],
    defaultNS: 'customer',
    ns: ['customer'],
    resources: {
      cs: { customer: csCustomer },
      en: { customer: enCustomer },
      sk: { customer: skCustomer },
      pseudo: { customer: pseudoCustomer },
    },
    interpolation: { escapeValue: false },
    returnNull: false,
  });

if (typeof document !== 'undefined') {
  document.documentElement.lang = (() => {
    if (initialLng === 'pseudo') {
      return 'pseudo';
    }
    return initialLng;
  })();
}

i18n.on('languageChanged', (lng) => {
  if (lng === 'pseudo') {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = 'pseudo';
    }
    return;
  }
  const normalized = normalizeCustomerLocale(lng);
  writeStoredCustomerLocale(normalized);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = normalized;
  }
});

export default i18n;
